import React from 'react';
import { Calculator, Users, Calendar, MapPin } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент боковой панели для быстрой настройки параметров поездки
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  setErrors,
  errors,
  isCalculating,
  russianDestinations
}) => {
  // Расчет текущего бюджета поездки
  const estimateBasicCost = () => {
    // Получаем данные текущего направления
    const destination = russianDestinations.find(d => d.id === formData.destination) || { costIndex: 1 };
    const destinationIndex = destination.costIndex || 1;

    // Базовые показатели стоимости на человека в день
    const baseCosts = {
      budget: 3000,
      midrange: 6000,
      luxury: 12000
    };

    // Определяем базовую стоимость в зависимости от типа поездки
    let baseCostPerDay;
    
    if (formData.accommodationType === 'hostel' || formData.foodOption === 'budget') {
      baseCostPerDay = baseCosts.budget;
    } else if (
      (formData.accommodationType === 'hotel' && formData.accommodationRating >= 4) || 
      formData.foodOption === 'premium' || 
      formData.transportClass === 'business'
    ) {
      baseCostPerDay = baseCosts.luxury;
    } else {
      baseCostPerDay = baseCosts.midrange;
    }
    
    // Применяем индекс стоимости для направления
    const adjustedCostPerDay = baseCostPerDay * destinationIndex;
    
    // Количество дней и путешественников
    const days = parseFloat(formData.tripDuration) || 7;
    const travelers = parseFloat(formData.travelers) || 1;
    
    // Расчет приблизительной стоимости
    const totalEstimatedCost = adjustedCostPerDay * days * travelers;
    
    return {
      total: totalEstimatedCost,
      perPerson: totalEstimatedCost / travelers,
      perPersonPerDay: adjustedCostPerDay
    };
  };
  
  // Получаем оценку стоимости
  const estimatedCost = estimateBasicCost();
  
  // Обработчик изменения продолжительности поездки
  const handleDurationChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      tripDuration: newValue
    }));
    
    // Очищаем ошибку если она есть
    if (errors.tripDuration) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.tripDuration;
        return newErrors;
      });
    }
  };
  
  // Обработчик изменения количества путешественников
  const handleTravelersChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      travelers: newValue
    }));
    
    // Очищаем ошибку если она есть
    if (errors.travelers) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.travelers;
        return newErrors;
      });
    }
  };
  
  // Предустановленные варианты поездок
  const presetTrips = [
    { 
      id: 'budget', 
      name: 'Бюджетно', 
      settings: {
        transportType: 'bus',
        transportClass: 'economy',
        accommodationType: 'hostel',
        accommodationRating: 2,
        foodOption: 'budget',
        entertainmentLevel: 'minimal',
        includeShoppingBudget: false,
        includeGuidedTours: false
      }
    },
    { 
      id: 'standard', 
      name: 'Стандарт', 
      settings: {
        transportType: 'train',
        transportClass: 'economy',
        accommodationType: 'hotel',
        accommodationRating: 3,
        foodOption: 'moderate',
        entertainmentLevel: 'moderate',
        includeShoppingBudget: true,
        shoppingBudget: 'moderate',
        includeGuidedTours: true
      }
    },
    { 
      id: 'comfort', 
      name: 'Комфорт', 
      settings: {
        transportType: 'plane',
        transportClass: 'comfort',
        accommodationType: 'hotel',
        accommodationRating: 4,
        foodOption: 'moderate',
        entertainmentLevel: 'moderate',
        includeShoppingBudget: true,
        shoppingBudget: 'moderate',
        includeGuidedTours: true
      }
    },
    { 
      id: 'luxury', 
      name: 'Премиум', 
      settings: {
        transportType: 'plane',
        transportClass: 'business',
        accommodationType: 'hotel',
        accommodationRating: 5,
        foodOption: 'premium',
        entertainmentLevel: 'extensive',
        includeShoppingBudget: true,
        shoppingBudget: 'extensive',
        includeGuidedTours: true
      }
    }
  ];
  
  // Применение предустановленной поездки
  const applyPresetTrip = (presetId) => {
    const preset = presetTrips.find(p => p.id === presetId);
    if (!preset) return;
    
    setFormData(prev => ({
      ...prev,
      ...preset.settings
    }));
  };
  
  // Функция расчета
  const calculateTrip = () => {
    // Здесь должна быть вызвана функция основного расчета
    // В данном случае просто эмулируем действие, так как основная логика 
    // находится в родительском компоненте
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-purple-500" />
        Быстрая настройка
      </h3>
      
      <div className="space-y-5">
        {/* Приблизительная стоимость поездки */}
        <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-100 dark:border-purple-800">
          <h4 className="text-sm font-medium text-purple-800 dark:text-purple-300 mb-2 flex items-center">
            <MapPin className="w-4 h-4 mr-1" />
            Примерная стоимость
          </h4>
          
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="bg-white dark:bg-gray-700 p-2 rounded border border-purple-100 dark:border-purple-800">
              <div className="text-gray-500 dark:text-gray-400 text-xs">Всего:</div>
              <div className="font-semibold text-purple-700 dark:text-purple-300">
                {formatCurrency(estimatedCost.total, 0)}
              </div>
            </div>
            
            <div className="bg-white dark:bg-gray-700 p-2 rounded border border-purple-100 dark:border-purple-800">
              <div className="text-gray-500 dark:text-gray-400 text-xs">На человека:</div>
              <div className="font-semibold text-purple-700 dark:text-purple-300">
                {formatCurrency(estimatedCost.perPerson, 0)}
              </div>
            </div>
            
            <div className="col-span-2 bg-white dark:bg-gray-700 p-2 rounded border border-purple-100 dark:border-purple-800">
              <div className="text-gray-500 dark:text-gray-400 text-xs">На человека в день:</div>
              <div className="font-semibold text-purple-700 dark:text-purple-300">
                {formatCurrency(estimatedCost.perPersonPerDay, 0)}
              </div>
            </div>
          </div>
        </div>
        
        {/* Продолжительность поездки */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="tripDurationSlider" className="text-gray-600 dark:text-gray-400 flex items-center">
              <Calendar className="w-4 h-4 mr-1 text-purple-500" />
              Продолжительность
            </label>
            <span className="font-medium text-gray-900 dark:text-white">
              {formData.tripDuration} {pluralize(parseInt(formData.tripDuration) || 0, 'день', 'дня', 'дней')}
            </span>
          </div>
          <input
            type="range"
            id="tripDurationSlider"
            min="1"
            max="30"
            step="1"
            value={formData.tripDuration}
            onChange={handleDurationChange}
            aria-label="Продолжительность поездки (дней)"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-600"
          />
          
          {/* Кнопки с предопределенными продолжительностями */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {[3, 7, 14, 21].map(days => (
              <button
                key={days}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, tripDuration: days.toString() }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.tripDuration === days.toString() 
                    ? 'bg-purple-100 border-purple-300 text-purple-700 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {days} {pluralize(days, 'день', 'дня', 'дней')}
              </button>
            ))}
          </div>
        </div>
        
        {/* Количество путешественников */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="travelersSlider" className="text-gray-600 dark:text-gray-400 flex items-center">
              <Users className="w-4 h-4 mr-1 text-purple-500" />
              Путешественники
            </label>
            <span className="font-medium text-gray-900 dark:text-white">
              {formData.travelers} {pluralize(parseInt(formData.travelers) || 0, 'человек', 'человека', 'человек')}
            </span>
          </div>
          <input
            type="range"
            id="travelersSlider"
            min="1"
            max="10"
            step="1"
            value={formData.travelers}
            onChange={handleTravelersChange}
            aria-label="Количество путешественников"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-600"
          />
          
          {/* Кнопки с предопределенными количествами путешественников */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {[1, 2, 3, 4].map(count => (
              <button
                key={count}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, travelers: count.toString() }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.travelers === count.toString() 
                    ? 'bg-purple-100 border-purple-300 text-purple-700 dark:bg-purple-900/30 dark:border-purple-700 dark:text-purple-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {count} {pluralize(count, 'чел.', 'чел.', 'чел.')}
              </button>
            ))}
          </div>
        </div>
        
        {/* Предустановленные типы поездок */}
        <div>
          <div className="mb-2 text-sm text-gray-600 dark:text-gray-400">
            Готовые варианты поездок:
          </div>
          <div className="grid grid-cols-2 gap-2">
            {presetTrips.map(preset => (
              <button
                key={preset.id}
                type="button"
                onClick={() => applyPresetTrip(preset.id)}
                className={`p-2 text-sm rounded-lg border transition-colors
                  bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 
                  dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-650
                `}
              >
                {preset.name}
              </button>
            ))}
          </div>
        </div>
        
        {/* Кнопка расчета */}
        <Button
          primary
          fullWidth
          onClick={calculateTrip}
          icon={<Calculator className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать поездку
        </Button>
      </div>
    </div>
  );
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, few, many) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return many;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return few;
  }
  return many;
};

export default QuickAdjustmentSidebar;